import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { useRef } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import type { ApBasisRefusal } from "@/accounts-payable/types";

const EXPLANATION =
  "Approving applies it to ServiceTitan as it is now, and Volca holds it to these values. To get proposals worked out from the current values instead, re-run the agent.";

/**
 * Asked when an approve comes back 409 `basis_changed`: what changed in
 * ServiceTitan since the action was proposed, and whether to approve it
 * against the current values. Confirming re-sends the approval with the
 * refusal's `basis_digest`, so it approves exactly the values shown here.
 */
export const BasisChangedDialog = ({
  refusal,
  isPending,
  onCancel,
  onConfirm,
}: {
  /** The refusal to confirm; null keeps the dialog closed. */
  refusal: ApBasisRefusal | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={refusal !== null}
      onOpenChange={(open) => !open && !isPending && onCancel()}
    >
      {/* Start on Cancel, the choice that changes nothing. */}
      <DialogContent initialFocus={cancelRef}>
        <DialogHeader className="pr-8">
          <DialogTitle>
            ServiceTitan changed since this was proposed
          </DialogTitle>
        </DialogHeader>
        <Alert color="warning" role="note">
          <WarningIcon />
          <AlertDescription>
            <Stack gap={1} render={<ul />}>
              {refusal?.changes.map((change) => (
                <li key={change.message}>{change.message}</li>
              ))}
            </Stack>
          </AlertDescription>
        </Alert>
        <DialogDescription>{EXPLANATION}</DialogDescription>
        <DialogFooter>
          <Button
            ref={cancelRef}
            variant="secondary"
            disabled={isPending}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button disabled={isPending} loading={isPending} onClick={onConfirm}>
            Approve with current values
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
